// <copyright file="quote-fabrication-buckets.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { FabricationJobState, QuoteItemFabricationStatus } from '../../../api/fabrication-job-api';
import {
  getFabricationJobStateCssName,
  getFabricationJobStateIcon,
  getFabricationJobStateLabel,
  getQuoteItemFabricationStatusIcon
} from '../../../fabrication-jobs/data/fabrication-job-helper-functions';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';

/**
 * A quote item's fabrication progress, as either surface counts it: one item (count 1) from the
 * quote items grid, or a pre-aggregated bucket from the quote list's summary endpoint.
 */
export interface FabricationStatusTally {
  fabricationStatus: QuoteItemFabricationStatus;
  fabricationJobState?: FabricationJobState | null;
  count: number;
}

export interface FabricationBucket {
  icon: string;
  badgeClass: string;
  label: string;
  count: number;
}

interface BucketDefinition {
  icon: string;
  badgeClass: () => string;
  label: () => string;
  matches: (tally: FabricationStatusTally) => boolean;
}

/** An item counted by its own fabrication status. */
function itemStatusBucket(
  status: QuoteItemFabricationStatus,
  badgeClass: string,
  label: () => string
): BucketDefinition {
  return {
    icon: getQuoteItemFabricationStatusIcon(status),
    badgeClass: () => badgeClass,
    label,
    matches: tally => tally.fabricationStatus === status
  };
}

/** An item counted by the state of the job it is assigned to. */
function jobStateBucket(state: FabricationJobState): BucketDefinition {
  return {
    icon: getFabricationJobStateIcon(state),
    badgeClass: () => `badge-${getFabricationJobStateCssName(state)}`,
    label: () => getFabricationJobStateLabel(state),
    matches: tally =>
      tally.fabricationStatus === QuoteItemFabricationStatus.AssignedToFabricationJob &&
      tally.fabricationJobState === state
  };
}

/**
 * The states a quote item can visibly be in, in lifecycle order.
 *
 * Assigned items are broken down by their JOB's state rather than counted under one "assigned"
 * bucket, because "assigned" says nothing about progress once you can see the job. Only the four
 * job states an assigned item can actually sit in are listed: a job reaching InProduction or
 * Completed fans the new status out to its items in the same transaction
 * (TaskTransitionFabricationJobState.ApplyItemFanOutAsync), so those buckets would be permanent
 * zeroes and would read as duplicates of the item-status buckets below.
 *
 * Hand-listed rather than derived from the enums so the order stays tied to the lifecycle if
 * either enum is ever renumbered.
 */
const bucketDefinitions: BucketDefinition[] = [
  itemStatusBucket(QuoteItemFabricationStatus.ReadyForFabrication, 'badge-info', () =>
    LangFranchiseeQuoteItemsView.readyForFabrication()
  ),
  jobStateBucket(FabricationJobState.Draft),
  jobStateBucket(FabricationJobState.Planned),
  jobStateBucket(FabricationJobState.AwaitingOrder),
  jobStateBucket(FabricationJobState.ReadyForProduction),
  itemStatusBucket(QuoteItemFabricationStatus.InProduction, 'badge-warning', () =>
    LangFranchiseeQuoteItemsView.inProduction()
  ),
  itemStatusBucket(QuoteItemFabricationStatus.ReadyForDelivery, 'badge-success', () =>
    LangFranchiseeQuoteItemsView.readyForDelivery()
  ),
  itemStatusBucket(QuoteItemFabricationStatus.Delivered, 'badge-secondary', () =>
    LangFranchiseeQuoteItemsView.delivered()
  )
];

/**
 * Totals the tallies into every bucket, in lifecycle order. Empty buckets come back at zero rather
 * than being dropped, so the result is the same shape whatever the quote contains and the caller
 * decides what to do with the empties: the quote list filters them out, since it shows one icon per
 * state actually present.
 */
export function tallyFabricationBuckets(entries: FabricationStatusTally[]): FabricationBucket[] {
  return bucketDefinitions.map(definition => ({
    icon: definition.icon,
    badgeClass: definition.badgeClass(),
    label: definition.label(),
    count: entries.reduce((total, entry) => (definition.matches(entry) ? total + entry.count : total), 0)
  }));
}
